import { Link } from 'react-router-dom';
import { SectionHead } from '../components/SectionHead';
import { MelaIDCardMock } from '../components/mocks/MelaIDCardMock';
import { WhatsAppMock } from '../components/mocks/WhatsAppMock';
import { DiscoveryStack } from '../components/mocks/DiscoveryStack';
import { IntegrationsOrbit } from '../components/mocks/IntegrationsOrbit';
import { BadgeStack } from '../components/mocks/BadgeStack';
import { RecallMock } from '../components/mocks/RecallMock';

type Variant = 'white' | 'saffron' | 'violet' | 'gold' | 'ink';

interface CellSpec {
  variant: Variant;
  span: string;
  tag: [string, string];
  heading: string;
  body: string;
  visual: React.ReactNode;
  to?: string;
  cta?: string;
}

const cells: CellSpec[] = [
  {
    variant: 'white',
    span: 'lg:col-span-3',
    tag: ['01', 'Identity'],
    heading: 'One ID. Every handshake.',
    body: 'A verified, multi-language business identity that lives across every event, every WhatsApp, every email signature. Free forever — the network is the moat.',
    visual: <MelaIDCardMock />,
  },
  {
    variant: 'saffron',
    span: 'lg:col-span-3',
    tag: ['02', 'AI Follow-up'],
    heading: 'Drafted in Gujarati. Reviewed by you.',
    body: 'Multilingual follow-ups in the lead’s preferred language, pulled only from content you uploaded — never invented. You see every draft before it sends.',
    visual: <WhatsAppMock />,
  },
  {
    variant: 'violet',
    span: 'lg:col-span-2',
    tag: ['03', 'Discovery'],
    heading: 'Every B2B event in India.',
    body: 'Vibrant Gujarat to IIJS. Discover, register, attend on one rail.',
    visual: <DiscoveryStack />,
    to: '/discover',
    cta: 'Browse all events',
  },
  {
    variant: 'ink',
    span: 'lg:col-span-4',
    tag: ['04', 'Intelligence'],
    heading: 'Built on India Stack.',
    body: 'WhatsApp, PAN and GST verification, and e-Sign live today. Aadhaar, DigiLocker, MCA21, ONDC, and Razorpay are on the rolling roadmap — added as the rails clear.',
    visual: <IntegrationsOrbit />,
  },
  {
    variant: 'gold',
    span: 'lg:col-span-2',
    tag: ['05', 'Trust'],
    heading: '6 tiers of verification.',
    body: 'PAN, GST, Aadhaar, MCA, Mela-Verified gold.',
    visual: <BadgeStack />,
  },
  {
    variant: 'white',
    span: 'lg:col-span-4',
    tag: ['06', 'Visitor superpower'],
    heading: 'Never forget a booth again.',
    body: 'A personalized microsite of every stall you visited — sent to your WhatsApp the day the event ends. Voice memos, photos, AI summaries. Yours forever.',
    visual: <RecallMock />,
  },
];

export function Bento() {
  return (
    <section className="px-6 sm:px-8 pt-24 sm:pt-32 pb-8" id="exhibitors">
      <div className="container-mela">
        <SectionHead
          eyebrow="The platform"
          title={
            <>
              Six layers Western tools never <span className="text-violet-600">built</span>.
            </>
          }
          lede="Identity. Discovery. Capture. Follow-up. Sign. Trust. Every layer Indian B2B has been waiting for, on one rail."
        />

        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {cells.map((c, i) => (
            <BentoCell key={i} {...c} />
          ))}
        </div>
      </div>
    </section>
  );
}

function BentoCell({ variant, span, tag, heading, body, visual, to, cta }: CellSpec) {
  const isInk = variant === 'ink';
  const variantClass = {
    white: 'bg-white border border-ink-200',
    saffron: 'bg-gradient-to-br from-saffron-50 to-saffron-100 border border-saffron-200',
    violet: 'bg-gradient-to-br from-violet-50 to-violet-100 border border-violet-200',
    gold: 'bg-gradient-to-br from-gold-50 to-gold-100 border border-gold-200',
    ink: 'bg-ink-900 text-white border border-ink-800',
  }[variant];

  const tagBg = {
    white: 'bg-ink-100 text-ink-700',
    saffron: 'bg-saffron-200 text-saffron-700',
    violet: 'bg-violet-200 text-violet-700',
    gold: 'bg-gold-100 text-gold-700',
    ink: 'bg-violet-600 text-white',
  }[variant];

  const ctaColor = {
    white: 'text-ink-900',
    saffron: 'text-saffron-700',
    violet: 'text-violet-700',
    gold: 'text-gold-700',
    ink: 'text-violet-300',
  }[variant];

  const articleClass = `group relative rounded-2xl p-8 min-h-[360px] h-full flex flex-col overflow-hidden transition-[transform,box-shadow] duration-base ease-mela hover:-translate-y-0.5 hover:shadow-elevated ${variantClass}`;

  const article = (
    <article className={`${articleClass} ${to ? 'cursor-pointer hover:shadow-floating' : ''}`}>
      {isInk && (
        <div
          className="absolute -top-[100px] -right-[100px] w-[400px] h-[400px] bg-[radial-gradient(circle,rgba(124,58,237,0.15),transparent_60%)] pointer-events-none"
          aria-hidden="true"
        />
      )}

      <div
        className={`font-mono text-[11px] tracking-wide uppercase mb-3 flex items-center gap-2 ${
          isInk ? 'text-violet-300' : 'text-ink-500'
        }`}
      >
        <span
          className={`w-[18px] h-[18px] rounded-[4px] text-[10px] font-bold flex items-center justify-center ${tagBg}`}
        >
          {tag[0]}
        </span>
        <span>{tag[1]}</span>
      </div>

      <h3
        className={`font-display text-[26px] sm:text-[30px] leading-[1.05] -tracking-[0.02em] font-bold mb-3 ${
          isInk ? 'text-white' : 'text-ink-900'
        }`}
      >
        {heading}
      </h3>

      <p className={`text-[15px] leading-snug max-w-[42ch] mb-auto ${isInk ? 'text-ink-300' : 'text-ink-600'}`}>
        {body}
      </p>

      {cta && (
        <div
          className={`mt-4 inline-flex items-center gap-1 text-[13px] font-semibold ${ctaColor} transition-transform duration-fast group-hover:translate-x-0.5`}
        >
          {cta} <span aria-hidden="true">→</span>
        </div>
      )}

      <div className="mt-8 flex items-center justify-center flex-1 relative z-[2]">{visual}</div>
    </article>
  );

  if (to) {
    return (
      <Link to={to} className={`block ${span}`} aria-label={`${heading} — ${cta ?? 'open'}`}>
        {article}
      </Link>
    );
  }
  return <div className={span}>{article}</div>;
}
